/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-negated-condition */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import { DEMANDS_QUERY, DEMANDS_SUBSCRIPTION_QUERY } from 'utils/CommonGraphQL';
import { dashboardDemandsColumns } from 'pages/Dashboard/components/constant';
import { cloneDeep, isEmpty } from 'lodash';
import { defaultDeleteFilter, showSequenceNumber } from 'utils/constants';
import Loader from 'components/common/Loader';
import { FaChevronDown } from 'react-icons/fa6';
import '../../../../dashboard-mobile.css';

/* ─── helpers ─── */
const getDemandName = (row) => {
  if (row?.demandCreatedFor === 'NON_REGISTERED_USER') {
    return row?.nonRegisteredUserName || 'Not Available';
  }
  return row?.trader?.entityName || 'Not Available';
};

const getDemandPhone = (row) => {
  if (row?.demandCreatedFor === 'NON_REGISTERED_USER') {
    const cc = row?.nonRegisteredUserCallingCode || '';
    const ph = row?.nonRegisteredUserPhoneNumber || '';
    return ph ? `${cc}${cc ? '-' : ''}${ph}` : 'No phone';
  }
  const cc = row?.trader?.callingCode?.callingCode || '';
  const ph = row?.trader?.phone || '';
  return ph ? `${cc}${cc ? '-' : ''}${ph}` : 'No phone';
};

const getGradeLabel = (row) =>
  (row?.grades || [])
    .map((g) => g?.name)
    .filter(Boolean)
    .join(', ') || 'N/A';

const getLocation = (row) => {
  if (row?.portsOfDischarge?.length) {
    return row.portsOfDischarge
      .map((p) => p?.name)
      .filter(Boolean)
      .join(', ');
  }
  if (row?.portOfDischarge?.name) return row.portOfDischarge.name;
  return (
    [row?.city?.cityName, row?.state?.stateName].filter(Boolean).join(', ') ||
    'N/A'
  );
};

const getQty = (val, row) => {
  if (val === null || val === undefined) return 'N/A';
  return `${val} ${row?.actualQuantityUnit || row?.quantityUnit || ''}`.trim();
};

const getRate = (row) => {
  if (row?.rate === null || row?.rate === undefined) return 'N/A';
  const cur = row?.rateUnit?.isoCurrencyCode || '';
  return `${cur ? `${cur} ` : ''}${row?.rate}/${row?.quantityUnit || ''}`.trim();
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusTone = (status) => {
  const s = `${status || ''}`.toLowerCase();
  if (s.includes('active') || s.includes('live')) return 'success';
  if (s.includes('pending') || s.includes('forward')) return 'warning';
  if (s.includes('inactive') || s.includes('expired') || s.includes('foreign'))
    return 'danger';
  return 'muted';
};

const DEMAND_TABS = ['Overview', 'Product'];

/* ─── component ─── */
const NewDemands = () => {
  const [filters, setFilters] = useState({
    filter: [
      { ...defaultDeleteFilter },
      { key: 'totalQty', value: 0, type: 'INT', op: 'GT' },
      { key: 'status', value: 'ACTIVE', type: 'STRING', op: 'EQ' },
    ],
    pageNo: 1,
    pageSize: 10,
    sortCol: 'createdAt',
    sortDirection: 'DESC',
  });

  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeTabs, setActiveTabs] = useState({});

  const { data, loading, error, subscribeToMore } = useQuery(DEMANDS_QUERY, {
    variables: { ...filters },
    fetchPolicy: 'network-only',
  });

  const findIndexOfItem = (item) =>
    dashboardDemandsColumns.findIndex((itm) => itm.accessorKey === item);

  dashboardDemandsColumns[findIndexOfItem('SNo')].cell = (cell) =>
    showSequenceNumber(cell, filters);

  useEffect(() => {
    const deletedObj = filters.filter.filter((val) => val.key !== 'isDeleted');
    const unsubscribe = subscribeToMore({
      document: DEMANDS_SUBSCRIPTION_QUERY,
      variables: { ...filters, filter: deletedObj },
      updateQuery: (prev, { subscriptionData }) => {
        const newData = cloneDeep(prev);
        const indexFound = newData.demands.demands.findIndex(
          (val) => val.id === subscriptionData.data.demands.demand.id,
        );
        if (!prev || isEmpty(prev)) return '';
        if (!subscriptionData.data) return prev;
        if (subscriptionData.data.demands.action === 'CREATED') {
          const newDemand = subscriptionData.data.demands.demand;
          const updatedDemands = [newDemand, ...newData.demands.demands];
          const newTotalCount = prev.demands.totalCount + 1;
          const newTotalPages = Math.ceil(newTotalCount / filters.pageSize);
          newData.demands = {
            ...newData.demands,
            totalCount: newTotalCount,
            totalPages: newTotalPages,
            demands: updatedDemands,
          };
        }
        if (subscriptionData.data.demands.action === 'UPDATED') {
          newData.demands.demands[indexFound] =
            subscriptionData.data.demands.demand;
        }
        if (subscriptionData.data.demands.action === 'DELETED') {
          const { id } = subscriptionData.data.demands.demand;
          const updatedDemands = prev.demands.demands.filter(
            (val) => val.id !== id,
          );
          const newTotalCount = prev.demands.totalCount - 1;
          const newTotalPages = Math.ceil(newTotalCount / filters.pageSize);
          newData.demands = {
            ...newData.demands,
            totalCount: newTotalCount,
            totalPages: newTotalPages,
            demands: updatedDemands,
          };
        }
        return newData;
      },
    });
    return () => unsubscribe();
  }, []);

  if (error) return `There is an error on this page : ${error.message} `;
  if (loading) return <Loader />;
  if (!data?.demands?.demands) return 'Demands not available';

  const { totalCount, totalPages } = data.demands;

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo: totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };

  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : { id: filters.sortCol, desc: filters.sortDirection === 'DESC' },
  ];

  const rows = data?.demands?.demands || [];

  return (
    <>
      {/* ── Mobile Card List ── */}
      <div className="dashboard-mobile-list">
        {rows.map((row, index) => {
          const cardId = row?.id || `demand-${index}`;
          const isExpanded = expandedCardId === cardId;
          const activeTab = activeTabs[cardId] || DEMAND_TABS[0];
          const seqNo =
            ((filters.pageNo || 1) - 1) * (filters.pageSize || 10) + index + 1;

          const overviewRows = [
            { label: 'S.No', value: seqNo },
            { label: 'Demand ID', value: row?.id || 'N/A' },
            { label: 'Buyer', value: getDemandName(row) },
            { label: 'Phone', value: getDemandPhone(row) },
            { label: 'Type', value: row?.demandType || 'N/A', tone: 'badge' },
            { label: 'Status', value: row?.status || 'N/A', tone: 'status' },
          ];

          const productRows = [
            { label: 'Grade', value: getGradeLabel(row) },
            { label: 'Location', value: getLocation(row) },
            { label: 'Total Qty', value: getQty(row?.totalQty, row) },
            { label: 'Min Qty', value: getQty(row?.minimumQty, row) },
            { label: 'Rate', value: getRate(row) },
            { label: 'Created', value: formatDate(row?.createdAt) },
          ];

          const activeSection =
            activeTab === 'Overview' ? overviewRows : productRows;

          return (
            <div className="dashboard-mobile-card" key={cardId}>
              <div className="dashboard-mobile-card__summary">
                <div className="dashboard-mobile-card__identity">
                  <span className="dashboard-mobile-card__name">
                    {getDemandName(row)}
                  </span>
                  <div className="dashboard-mobile-card__meta">
                    {`Demand ID ${row?.id || 'N/A'} | ${getDemandPhone(row)}`}
                  </div>
                </div>
                <div className="dashboard-mobile-card__aside">
                  <span
                    className={`dashboard-mobile-card__pill ${getStatusTone(row?.status)}`}
                  >
                    {row?.status || 'N/A'}
                  </span>
                  <div className="dashboard-mobile-card__tools">
                    <button
                      type="button"
                      className={`dashboard-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={() =>
                        setExpandedCardId((prev) =>
                          prev === cardId ? null : cardId,
                        )
                      }
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="dashboard-mobile-card__body">
                  <div className="dashboard-mobile-card__tabs">
                    {DEMAND_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`dashboard-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() =>
                          setActiveTabs((prev) => ({ ...prev, [cardId]: tab }))
                        }
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="dashboard-mobile-card__detail-list">
                    {activeSection.map((item) => (
                      <div
                        className="dashboard-mobile-card__detail-row"
                        key={item.label}
                      >
                        <div className="dashboard-mobile-card__detail-label">
                          {item.label}
                        </div>
                        <div className="dashboard-mobile-card__detail-value">
                          {item.tone === 'badge' || item.tone === 'status' ? (
                            <span
                              className={`dashboard-mobile-card__pill ${getStatusTone(item.value)}`}
                            >
                              {item.value}
                            </span>
                          ) : (
                            item.value
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Mobile Pagination ── */}
      {totalPages ? (
        <div className="dashboard-mobile-pagination">
          <span className="dashboard-mobile-pagination__count">
            {filters.pageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal dashboard-mobile-pagination__select"
            value={filters.pageSize}
            onChange={(e) =>
              handlePageChange({ pageNo: 1, pageSize: Number(e.target.value) })
            }
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="dashboard-mobile-pagination__actions">
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: 1, pageSize: filters.pageSize })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo - 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo + 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Desktop Table (hidden on mobile) ── */}
      <div className="dashboard-mobile-hide-table">
        <FSBTable
          data={data?.demands?.demands}
          columns={dashboardDemandsColumns}
          totalCount={totalCount || 0}
          totalPages={totalPages || 0}
          currentPageNo={filters.pageNo}
          pageSize={filters.pageSize}
          keyField="id"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination={false}
          remote
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handlePageChange}
          onSortChange={onSortChange}
          sorting={sortBy}
          searchPlaceholder="Search Demands"
        />
      </div>
    </>
  );
};

export default NewDemands;
